import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useLocalizedNumericSeparators } from '@/hooks/useLocalizedNumericSeparators';
import classes from '@/Screens/Dashboard/Flows/DepositFlow/DepositAmount.module.scss';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly value: number | null;
  readonly currency: CurrencyCodeType | '';

  onChange(value: number | null): void;
}

export const AmountInputWithFloatingCurrency: React.FC<Props> = (
  props: Props,
): React.ReactElement => {
  const { value, currency, onChange } = props;

  const [currencyBox, setIntersectionDetector] = React.useState<HTMLElement | null>(null);
  const [input, setInput] = React.useState<HTMLInputElement | null>(null);
  const [fontSize, setFontSize] = React.useState<number>(88);

  const separators = useLocalizedNumericSeparators();
  const formatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const onInputChange = React.useCallback(
    (values: NumberFormatValues): void => {
      if (values.floatValue === undefined) {
        onChange(null);
      } else {
        onChange(values.floatValue);
      }
    },
    [onChange],
  );

  const currencyStyle = React.useMemo((): React.CSSProperties => {
    if (!input) {
      return {};
    }

    if (value === null) {
      return {};
    }

    const formattedValue = formatter.format(value);
    const style = getComputedStyle(input);
    const width = computeTextWidthAtFontSize(style.fontFamily, fontSize, formattedValue);

    return {
      left: `${width}px`,
    };
  }, [input, value, formatter, fontSize]);

  const getOptimalFontSize = React.useCallback(
    (container: HTMLElement): number => {
      if (input !== null) {
        if (value === null) {
          return 88;
        }

        const formattedValue = formatter.format(value);
        const targetString = `${formattedValue}${currency}`;
        const style = getComputedStyle(input);

        for (let fontSize = 88; fontSize >= 10; fontSize -= 1) {
          const neededWidth = computeTextWidthAtFontSize(style.fontFamily, fontSize, targetString);
          if (neededWidth <= container.offsetWidth - 4) {
            return fontSize;
          }
        }
      }

      return 10;
    },
    [currency, formatter, input, value],
  );

  React.useEffect((): VoidFunction | void => {
    if (currencyBox === null) {
      return;
    }

    const root = currencyBox.parentElement?.parentElement as HTMLElement | null;
    if (root === null) {
      return;
    }

    const options = {
      root: root,
      threshold: 1,
    };

    const updateFontSize = (): void => {
      setTimeout((): void => {
        setFontSize(getOptimalFontSize(root));
      }, 0);
    };
    // Call it now and then install the observer
    updateFontSize();

    // Call it whenever we're out of the viewport
    const observer = new IntersectionObserver(updateFontSize, options);

    observer.observe(currencyBox);
    return (): void => {
      observer.disconnect();
    };
  }, [currencyBox, getOptimalFontSize]);

  const sanitizedValue = React.useMemo((): number | '' => {
    return value === null ? '' : value;
  }, [value]);

  return (
    <Box sx={styles.inputContainer} minWidth={0} my={1} fontSize={fontSize}>
      <Typography
        fontSize="inherit"
        letterSpacing="inherit"
        fontWeight="inherit"
        lineHeight="inherit"
        color="text.primary">
        $
      </Typography>
      <Box position="relative">
        <NumericFormat
          className={classes.input}
          getInputRef={setInput}
          value={sanitizedValue}
          thousandSeparator={separators.groupSeparator}
          decimalSeparator={separators.decimalSeparator}
          fixedDecimalScale={true}
          autoFocus={true}
          decimalScale={2}
          aria-label="Amount"
          onValueChange={onInputChange}
        />
        <Box sx={styles.currencyBox} style={currencyStyle}>
          <Typography
            fontSize="inherit"
            letterSpacing="inherit"
            fontWeight="inherit"
            lineHeight="inherit"
            color="custom.LightGray">
            {currency}
          </Typography>
          <Box ref={setIntersectionDetector} width="5px" height="100%" />
        </Box>
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    height: 110,
    overflow: 'hidden',
    letterSpacing: '-0.02em',
    fontWeight: 300,
  },
  currencyBox: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    inset: 0,
    height: 110,
    py: 1,
    pointerEvents: 'none',
  },
  input: {
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 88,
    fontWeight: 300,
    lineHeight: '110px',
    letterSpacing: 'inherit',
    width: 'auto',
  },
  link: {
    display: 'inline',
    textDecoration: 'underline',
  },
};

const canvas = document.createElement('canvas');
const context = canvas.getContext('2d');

const computeTextWidthAtFontSize = (fontFamily: string, fontSize: number, text: string): number => {
  if (context !== null) {
    context.font = `300 ${fontSize}px ${fontFamily}`;
    context.letterSpacing = '-0.02em';

    const measure = context.measureText(text);

    return measure.width;
  } else {
    return 0;
  }
};
